import React, { useEffect, useState, useMemo } from 'react';

import { Alert, Center, Loader, Skeleton, Stack, Text, Divider } from '@mantine/core';
import { IconExclamationCircle, IconAlertCircle } from '@tabler/icons-react';
import { IEventTypeMultiSelectAutoComplete } from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import useCreateSelfBookingAppointment from 'hooks/appointmentSelfBooking/useCreateSelfBookingAppointment';
import useSelfBookingAppointmentAthleteHasValidCredit from 'hooks/appointmentSelfBooking/useSelfBookingAppointmentAthleteHasValidCredit';
import useSelfBookingAppointmentType from 'hooks/appointmentSelfBooking/useSelfBookingAppointmentType';
import useSelfBookingAppointmentTypes from 'hooks/appointmentSelfBooking/useSelfBookingAppointmentTypes';
import { useAthleteTracking } from 'hooks/mixpanel/useAthleteTracking';
import useAthleteSalesPortalPackages from 'hooks/salesPortal/useAthleteSalesPortalPackages';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';
import { SelfBookingAppointmentType } from 'types';
import { SelfBookingAppointmentCoachAvailableTimeSlot, SelfBookingAppointmentCreate } from 'types/generated';
import notificationHandler from 'utils/notifications';

import AppointmentTypeCard from './components/AppointmentTypeCard';
import SelfBookingAppointmentCoachSelectionStep from './components/SelfBookingAppointmentCoachSelectionStep';
import SelfBookingAppointmentDateSelectionStep from './components/SelfBookingAppointmentDateSelectionStep';
import SelfBookingAppointmentHeader from './components/SelfBookingAppointmentHeader';
import SelfBookingAppointmentTimeSlotSelectionStep from './components/SelfBookingAppointmentTimeSlotSelectionStep';
import { SelfBookingAppointmentCreateSteps } from './components/types';

interface BookingStepState {
  selectedAppointmentTypeId: string | null;
  selectedStaffId: string | null;
  selectedDate: Date | null;
  selectedTimeSlot: SelfBookingAppointmentCoachAvailableTimeSlot | null;
  currentMonth: Date;
}

interface BookingState {
  step: SelfBookingAppointmentCreateSteps;
  stepState: BookingStepState;
}

interface FilterTerms {
  eventTypes: IEventTypeMultiSelectAutoComplete[];
  coaches: string[];
  locations: string[];
}

interface AppointmentsViewProps {
  searchFilters?: FilterTerms;
  currentStep: BookingState;
  setCurrentStep: React.Dispatch<React.SetStateAction<BookingState>>;
  onReset?: () => void;
}

const AppointmentsView = ({ currentStep, setCurrentStep }: AppointmentsViewProps): JSX.Element => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { errorNotification } = notificationHandler();
  const { trackReservationBlockedByDailyLimit } = useAthleteTracking();

  const [appointmentTypes, setAppointmentTypes] = useState<SelfBookingAppointmentType[]>([]);

  const {
    data,
    isError: isSelfBookableAppointmentTypesError,
    isLoading: isSelfBookableAppointmentTypesLoading,
    isSuccess: isSelfBookableAppointmentTypesSuccess,
    fetchNextPage,
    hasNextPage,
  } = useSelfBookingAppointmentTypes({
    ordering: 'name',
  });

  const {
    data: selfBookableAppointmentTypeData,
    isSuccess: isSelfBookableAppointmentTypeSuccess,
    isError: isSelfBookableAppointmentTypeError,
    error: selfBookableAppointmentTypeError,
    isLoading: isSelfBookableAppointmentTypeLoading,
  } = useSelfBookingAppointmentType(currentStep.stepState.selectedAppointmentTypeId);

  const {
    data: athleteHasValidCreditData,
    isLoading: isAthleteHasValidCreditLoading,
    isError: isAthleteHasValidCreditError,
    isSuccess: isAthleteHasValidCreditSuccess,
  } = useSelfBookingAppointmentAthleteHasValidCredit({
    appointmentTypeId: currentStep.stepState.selectedAppointmentTypeId,
    startDate: currentStep.stepState.selectedDate
      ? dayjs(currentStep.stepState.selectedDate).format('YYYY-MM-DD')
      : undefined,
    enabled:
      !!currentStep.stepState.selectedAppointmentTypeId &&
      !!currentStep.stepState.selectedDate &&
      currentStep.step === 'timeSlot',
  });

  const { data: athleteSalesPortalPackagesData, isSuccess: isAthleteSalesPortalPackagesSuccces } =
    useAthleteSalesPortalPackages(session?.gym?.gymId, undefined, session?.profile?.profileId);

  const hasSalesPortalPackages =
    isAthleteSalesPortalPackagesSuccces && !!athleteSalesPortalPackagesData?.data?.length;

  const athleteHasValidCredit = !!athleteHasValidCreditData?.data?.hasValidCredits;

  const createSelfBookingAppointment = useCreateSelfBookingAppointment(
    currentStep.stepState.selectedAppointmentTypeId || '',
  );

  const selectedCoach = useMemo(() => {
    if (selfBookableAppointmentTypeData?.data.selfBookingCoaches && currentStep.stepState.selectedStaffId) {
      return selfBookableAppointmentTypeData.data.selfBookingCoaches.find(
        c => c.staffId === currentStep.stepState.selectedStaffId,
      );
    }
    return null;
  }, [selfBookableAppointmentTypeData?.data.selfBookingCoaches, currentStep.stepState.selectedStaffId]);

  const handleSelfBookAppointmentType = (): void => {
    if (
      currentStep.stepState.selectedTimeSlot &&
      currentStep.stepState.selectedAppointmentTypeId &&
      currentStep.stepState.selectedStaffId
    ) {
      const payload: SelfBookingAppointmentCreate = {
        appointmentType: currentStep.stepState.selectedAppointmentTypeId,
        staff: currentStep.stepState.selectedStaffId,
        startDateTime: currentStep.stepState.selectedTimeSlot.startDateTime,
      };
      createSelfBookingAppointment.mutate(payload, {
        onError: error => {
          const errorData = error.response?.data;
          if (errorData?.errorCode === 'APPOINTMENT_LIMIT_ONE_PER_DAY') {
            trackReservationBlockedByDailyLimit('appointments', errorData?.details?.heldBy ?? 'reservation');
          }
          errorNotification('Error to self book an appointment', errorData?.message);
        },
        onSuccess: response => {
          navigate(`/appointments/${response?.data.appointmentId}`);
        },
      });
    }
  };

  const handleCoachSelect = (staffId: string): void => {
    setCurrentStep(prev => ({
      ...prev,
      step: 'date',
      stepState: {
        ...prev.stepState,
        selectedStaffId: staffId,
      },
    }));
  };

  const handleDateSelect = (date: Date): void => {
    setCurrentStep(prev => ({
      ...prev,
      step: 'timeSlot',
      stepState: {
        ...prev.stepState,
        selectedDate: date,
      },
    }));
  };

  const handleTimeSlotSelect = (timeSlot: SelfBookingAppointmentCoachAvailableTimeSlot): void => {
    setCurrentStep(prev => ({
      ...prev,
      stepState: {
        ...prev.stepState,
        selectedTimeSlot: timeSlot,
      },
    }));
  };

  useEffect(() => {
    if (isSelfBookableAppointmentTypesSuccess && data?.pages) {
      setAppointmentTypes(data.pages.map(page => page.data.results).flat());
    }
  }, [data?.pages, isSelfBookableAppointmentTypesSuccess]);

  const renderAppointmentBookingFlow = (): JSX.Element => {
    if (
      currentStep.stepState.selectedAppointmentTypeId &&
      isSelfBookableAppointmentTypeSuccess &&
      selfBookableAppointmentTypeData?.data
    ) {
      const appointmentType = selfBookableAppointmentTypeData.data;

      return (
        <>
          <SelfBookingAppointmentHeader
            appointmentName={appointmentType.name}
            duration={appointmentType.duration}
            locationName={appointmentType.location?.name}
            selectedCoach={selectedCoach}
            selectedDate={currentStep.stepState.selectedDate}
          />

          <Divider my='md' />

          {currentStep.step === 'coach' && (
            <SelfBookingAppointmentCoachSelectionStep
              coaches={appointmentType.selfBookingCoaches}
              athleteHasCredit={appointmentType.athleteHasCredit}
              hasSalesPortalPackages={hasSalesPortalPackages}
              onSelectCoach={handleCoachSelect}
            />
          )}

          {currentStep.step === 'date' && (
            <SelfBookingAppointmentDateSelectionStep
              appointmentTypeId={currentStep.stepState.selectedAppointmentTypeId}
              staffId={currentStep.stepState.selectedStaffId || ''}
              currentMonth={currentStep.stepState.currentMonth}
              selectedDate={currentStep.stepState.selectedDate}
              onMonthChange={month =>
                setCurrentStep(prev => ({
                  ...prev,
                  stepState: {
                    ...prev.stepState,
                    currentMonth: month,
                  },
                }))
              }
              onDateSelect={handleDateSelect}
            />
          )}

          {currentStep.step === 'timeSlot' && currentStep.stepState.selectedDate && (
            <SelfBookingAppointmentTimeSlotSelectionStep
              appointmentTypeId={currentStep.stepState.selectedAppointmentTypeId}
              staffId={currentStep.stepState.selectedStaffId || ''}
              selectedDate={currentStep.stepState.selectedDate}
              selectedTimeSlot={currentStep.stepState.selectedTimeSlot}
              athleteHasValidCredit={athleteHasValidCredit}
              isAthleteHasValidCreditLoading={isAthleteHasValidCreditLoading}
              isAthleteHasValidCreditError={isAthleteHasValidCreditError}
              isAthleteHasValidCreditSuccess={isAthleteHasValidCreditSuccess}
              hasSalesPortalPackages={hasSalesPortalPackages}
              isBooking={createSelfBookingAppointment.isLoading}
              onSelectTimeSlot={handleTimeSlotSelect}
              onBookAppointment={handleSelfBookAppointmentType}
            />
          )}
        </>
      );
    }

    if (currentStep.stepState.selectedAppointmentTypeId) {
      if (isSelfBookableAppointmentTypeLoading) {
        return (
          <>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} h='42px' />
            ))}
          </>
        );
      }

      if (isSelfBookableAppointmentTypeError) {
        return (
          <Alert icon={<IconAlertCircle size={16} />} title='Error' color='red' mb='md'>
            {selfBookableAppointmentTypeError?.response?.data.message
              ? selfBookableAppointmentTypeError?.response?.data.message
              : 'Unable to fetch self-bookable appointment type. Please try again.'}
          </Alert>
        );
      }
    }

    return <></>;
  };

  return (
    <Stack>
      {currentStep.stepState.selectedAppointmentTypeId ? (
        renderAppointmentBookingFlow()
      ) : (
        <>
          {isSelfBookableAppointmentTypesLoading && (
            <Stack spacing='md'>
              <Skeleton height={100} />
              <Skeleton height={100} />
              <Skeleton height={100} />
            </Stack>
          )}

          {!isSelfBookableAppointmentTypesLoading && appointmentTypes.length === 0 && (
            <Center>
              <Text color='dimmed'>No appointment types available for self-booking.</Text>
            </Center>
          )}

          {isSelfBookableAppointmentTypesError && (
            <Center sx={{ flexGrow: 1 }} p='lg'>
              <Alert icon={<IconExclamationCircle size='1rem' />} title='Error' color='red'>
                Something went wrong while fetching appointment types!
              </Alert>
            </Center>
          )}

          {appointmentTypes.length > 0 && (
            <>
              <Text size='lg' ta='center' my='md'>
                Select the appointment you want to book
              </Text>

              <InfiniteScroll
                dataLength={appointmentTypes.length}
                next={fetchNextPage}
                hasMore={!!hasNextPage}
                loader={
                  <Center py='sm'>
                    <Loader />
                  </Center>
                }
                scrollableTarget='scrollable-content'
              >
                <Stack spacing='md' pb='xl'>
                  {appointmentTypes.map(appointmentType => (
                    <AppointmentTypeCard
                      key={appointmentType.appointmentTypeId}
                      appointmentType={appointmentType}
                      onSelect={(appointmentTypeId: string) => {
                        setCurrentStep(prev => ({
                          ...prev,
                          step: 'coach',
                          stepState: {
                            ...prev.stepState,
                            selectedAppointmentTypeId: appointmentTypeId,
                          },
                        }));
                      }}
                    />
                  ))}
                </Stack>
              </InfiniteScroll>
            </>
          )}
        </>
      )}
    </Stack>
  );
};

export default AppointmentsView;
